"use client";

import { useEffect, useRef, useState } from "react";
import {
  Modal,
  Text,
  Button,
  Stack,
  Title,
  Anchor,
  FocusTrap,
} from "@mantine/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { authenticatedFetch } from "@/app/_utils/authenticatedFetch";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import {
  useIsSubscriptionReady,
  useRefreshSubscription,
  useSubscriptionContext,
} from "@/app/_hooks/subscription/useSubscriptionContext";
import { migrationStageFor } from "@/app/_constants/subscriptionMigration";

dayjs.extend(utc);
dayjs.extend(timezone);

const SUPPORT_EMAIL = "contact@mountmonitor.com";

// TEMP PREVIEW: fake flag so the modal shows on every page in dev.
// 1 = notice (dismissible, 7-day snooze), 2 = warning (last 14 days),
// 3 = blocked (from Oct 15). Set to 0 (or delete) when done.
const MOCK_STAGE: number = 0;

const readMockMigration = (): Subscription["nzMigration"] | null => {
  if (process.env.NODE_ENV !== "development") return null;
  if (!MOCK_STAGE) return null;
  const day = 24 * 60 * 60 * 1000;
  const requiredFrom =
    MOCK_STAGE === 3
      ? Date.now() - day
      : MOCK_STAGE === 2
      ? Date.now() + 9 * day
      : Date.now() + 30 * day;
  return { nzSubscriptionId: "sub_mock", requiredFrom };
};

// Prompts tenants who still bill on the old (NZ) Stripe account to re-confirm
// their card on the new one through a 30-second Checkout (Link autofills their
// saved card there). Driven by nzMigration on
// users/{uid}/metadata/subscription; the Atlas billing webhook clears that
// field when the Checkout completes, which removes this modal for good.
//
// Snooze state lives on the subscription doc (nzMigration.snoozedUntil, set
// by /api/subscription/migration-event), NOT in localStorage: private
// browsing and in-app browsers lose local state every session, which re-shows
// the modal and re-emails support on every visit. Same route throttles the
// "saw the prompt" emails server-side.
//
// Never shown while billing is past_due: the overdue modal owns the screen,
// and a migration mid-dunning would strand the unpaid NZ invoice.
export const BillingMigrationModal = () => {
  const user = useUserContext();
  const setAlert = useSetAlert();
  const subscription = useSubscriptionContext();
  const isSubscriptionReady = useIsSubscriptionReady();
  const refreshSubscription = useRefreshSubscription();
  // Optimistic hide for this page load right after dismissing; the server
  // snooze covers every load after it.
  const [justDismissed, setJustDismissed] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Set when we've just returned from a successful Checkout: hides the modal
  // while the webhook catches up, and re-fetches until the flag clears.
  const [justCompleted, setJustCompleted] = useState(false);
  // Read in an effect, not during render, so SSR and client markup agree.
  const [mockMigration, setMockMigration] = useState<
    Subscription["nzMigration"] | null
  >(null);
  useEffect(() => setMockMigration(readMockMigration()), []);

  const migration = mockMigration ?? subscription?.nzMigration;
  const isPastDue =
    !mockMigration && subscription?.billing?.status === "past_due";
  const isShown =
    (isSubscriptionReady || !!mockMigration) && !!migration && !isPastDue;
  const stage = migration ? migrationStageFor(migration.requiredFrom) : "notice";
  const isBlocked = stage === "blocked";
  const snoozed =
    !isBlocked &&
    (justDismissed || (migration?.snoozedUntil ?? 0) > Date.now());

  // Back from Checkout: give the webhook a moment instead of re-showing a
  // modal the tenant has just dealt with.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billingMigration") !== "success") return;
    setJustCompleted(true);
    params.delete("billingMigration");
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (query ? `?${query}` : "")
    );
    const timers = [2000, 5000, 10000].map((ms) =>
      setTimeout(() => refreshSubscription(), ms)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire and forget; the route decides whether support actually gets an
  // email (shown is throttled to one per 12h per tenant).
  const notify = (action: string, detail?: string) =>
    authenticatedFetch("/api/subscription/migration-event", {
      method: "POST",
      body: JSON.stringify({
        action,
        userName: user?.settings?.name || user?.details?.fullName || "Unknown",
        detail: detail ?? null,
      }),
    }).catch((err) =>
      console.error("Failed to notify billing migration action:", err)
    );

  // Once per mount (hard page load), never on mock; the server throttles.
  const reportedShown = useRef(false);
  useEffect(() => {
    if (!isShown || snoozed || justCompleted || mockMigration) return;
    if (reportedShown.current) return;
    reportedShown.current = true;
    notify("shown");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown, snoozed, justCompleted, mockMigration]);

  if (!isShown || justCompleted) return null;
  if (snoozed && !mockMigration) return null;

  const tz = user?.settings?.timezone || dayjs.tz.guess();
  const deadline = dayjs(migration.requiredFrom).tz(tz).format("MMMM D");
  const daysLeft = Math.max(
    0,
    dayjs(migration.requiredFrom).diff(dayjs(), "day")
  );

  const dismiss = () => {
    setJustDismissed(true);
    notify("dismissed");
  };

  const handleReconfirm = async () => {
    setIsRedirecting(true);
    try {
      const res = await authenticatedFetch("/api/subscription/migrate-checkout", {
        method: "POST",
      });
      const data = await res.json();
      if (data.status === "done") {
        // Already migrated (a completed Checkout the webhook missed, or done
        // by hand): refresh and get out of the way.
        await notify("checkout", "Already migrated, flag cleared");
        await refreshSubscription();
        return;
      }
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Failed to create checkout");
      }
      // Awaited: navigating away would cancel it otherwise.
      await notify("checkout");
      window.location.assign(data.url);
    } catch (error) {
      console.error("Failed to start billing migration checkout:", error);
      setIsRedirecting(false);
      setAlert({
        type: "error",
        title: "Unable to open the confirmation page",
        content: `Please try again, or contact us at ${SUPPORT_EMAIL}.`,
      });
    }
  };

  return (
    <Modal
      opened={true}
      onClose={isBlocked ? () => {} : dismiss}
      transitionProps={{ transition: "fade", duration: 300 }}
      title={
        <Title order={3} fw={700}>
          Billing Update Required
        </Title>
      }
      closeOnClickOutside={!isBlocked}
      closeOnEscape={!isBlocked}
      withCloseButton={!isBlocked}
      overlayProps={{
        backgroundOpacity: 0.35,
        style: {
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
      }}
      centered
      size="md"
    >
      <FocusTrap.InitialFocus />
      <Stack gap="md">
        <Text>
          We are upgrading our billing system and need you to re-confirm your
          payment details. It takes about 30 seconds, and your plan, price, and
          next billing date stay exactly the same.
        </Text>
        {isBlocked ? (
          <Text>
            This is now required before you can continue using MountMonitor.
            Confirm below and you will be right back in.
          </Text>
        ) : stage === "warning" ? (
          <Text>
            <Text span fw={600}>
              Just a heads up:
            </Text>{" "}
            from {deadline} ({daysLeft} {daysLeft === 1 ? "day" : "days"} away)
            this will be required before continuing to use MountMonitor.
          </Text>
        ) : (
          <Text>
            You can do this any time before{" "}
            <Text span fw={600}>
              {deadline}
            </Text>
            .
          </Text>
        )}

        <Stack gap="xs" mt="xs">
          <Button onClick={handleReconfirm} loading={isRedirecting} fullWidth>
            Confirm Payment Details
          </Button>
          {!isBlocked && (
            <Button variant="default" onClick={dismiss} fullWidth>
              Remind Me Later
            </Button>
          )}
        </Stack>

        <Text size="xs" c="dimmed">
          Any free days shown on the confirmation page are just your
          already-paid time carrying over. You will not be charged early or
          extra.
        </Text>

        <Text size="sm">
          Need a hand? Email{" "}
          <Anchor
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
              "Billing update"
            )}`}
            fw={600}
          >
            {SUPPORT_EMAIL}
          </Anchor>
          .
        </Text>
      </Stack>
    </Modal>
  );
};
